import { useEffect, useState } from "react";
import { startOfNextDay, toEpochSeconds, toLocalDate } from "../../domain/time";
import type { LocalDate } from "../../domain/time";

/**
 * Longest wait between two checks: a timer of a sleeping device can fire late, and the device clock
 * can be set, so the day is checked again at least once a minute (without a render if unchanged).
 */
export const todayCheckMaxDelayMs = 60_000;

/** Shortest wait between two checks, so a day that begins twice (clock set back) never spins. */
const todayCheckMinDelayMs = 1_000;

/**
 * Returns today's local date in a time zone and follows the change of the day: a timer runs to the
 * next local midnight of that zone (summer and winter time alike, {@link startOfNextDay}), at most a
 * minute at a time, and the date is checked again when the page becomes visible. The component
 * renders again only when the date changes (F-15).
 *
 * @param nowMs - Clock in epoch milliseconds (injected).
 * @param timeZone - IANA time zone of "today", usually that of the employment or the tenant.
 * @returns Today's local date.
 */
export function useToday(nowMs: () => number, timeZone: string): LocalDate {
  const [checked, setChecked] = useState(() => ({ atMs: nowMs(), timeZone }));
  let checkedAtMs = checked.atMs;

  if (checked.timeZone !== timeZone) {
    // Another zone (the employment became known): its day is computed from the current time at once,
    // not from the time of the last check (React renders again before it paints).
    checkedAtMs = nowMs();
    setChecked({ atMs: checkedAtMs, timeZone });
  }

  const today = toLocalDate(toEpochSeconds(checkedAtMs), timeZone);

  useEffect(() => {
    let handle: number | undefined;

    const check = (): void => {
      const currentMs = nowMs();

      if (toLocalDate(toEpochSeconds(currentMs), timeZone) !== today) {
        // The next render brings the new date and schedules the next check.
        setChecked({ atMs: currentMs, timeZone });
        return;
      }

      const untilNextDayMs = startOfNextDay(today, timeZone) * 1000 - currentMs;
      const delayMs = Math.min(
        Math.max(untilNextDayMs, todayCheckMinDelayMs),
        todayCheckMaxDelayMs,
      );
      handle = window.setTimeout(check, delayMs);
    };
    const checkWhenVisible = (): void => {
      if (document.visibilityState === "visible") {
        window.clearTimeout(handle);
        check();
      }
    };

    check();
    document.addEventListener("visibilitychange", checkWhenVisible);

    const stop = (): void => {
      window.clearTimeout(handle);
      document.removeEventListener("visibilitychange", checkWhenVisible);
    };

    return stop;
  }, [nowMs, timeZone, today]);

  return today;
}
